/**
 * BOR / AOR requests — building the letter and watching for the signature.
 *
 * ⚠️ THE SEND BUTTON DOES NOT SEND TO A CARRIER, AND THE PAGE SAYS SO
 * REPEATEDLY. Every other programme in the Requests Center emails a carrier
 * when you press send. This one emails one person — the insured — and nothing
 * reaches a carrier until they sign. An agent who presses this believing the
 * carriers have been told would stop chasing exactly when the work starts.
 *
 * ⚠️ AND A SIGNATURE DOES NOT SEND IT EITHER (changed 2026-09-12). Signing
 * moves the request to 'signed', where it waits for an agent to read the
 * signature and release it. Josh: "The document was signed, so it should just
 * say signed. Then we need to get it to the carrier… Shouldn't we confirm the
 * document has been correctly signed?" A BOR at the carrier notifies the
 * incumbent agent and cannot be recalled, so somebody looks first.
 *
 * ⚠️ AND THE AGENCY BEING APPOINTED IS THE MEMBER, NOT AGENTS ALLIANCE. Every
 * other letter here is sent on behalf of AAS. This one appoints the member's own
 * agency, so its name and address go on the letter as the thing being chosen —
 * which is why they are typed and remembered rather than read from our own
 * outbound identity.
 */
import { Fragment, useEffect, useState } from 'react';
import { api, type BorRequest, type BorReview, type CarrierLetter } from '../lib/api';
import { PageHero } from '../components/PageHero';

interface CarrierLine {
  carrier: string;
  carrierAddress: string;
  policyNumbers: string;
  policyEffective: string;
  to: string;
}

const blankCarrier = (): CarrierLine => ({
  carrier: '',
  carrierAddress: '',
  policyNumbers: '',
  policyEffective: '',
  to: '',
});

/**
 * The agency's own details, remembered on this device.
 *
 * ⚠️ THE SAME TRADE THE LOSS RUN SIGNATURE BLOCK MAKES, and for the same
 * reason: there is no address anywhere in the schema, the name is the only part
 * we can default, and asking somebody to retype their own letterhead on every
 * request is how a tool stops being used. It is per-device and per-browser,
 * which is the honest limit of it.
 */
const AGENCY_KEY = 'bor-agency';
const loadAgency = (): { name: string; address: string } => {
  try {
    return { name: '', address: '', ...(JSON.parse(localStorage.getItem(AGENCY_KEY) ?? '{}') as object) };
  } catch {
    return { name: '', address: '' };
  }
};

const when = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  signature: 'Awaiting signature',
  signed: 'Signed — ready to send',
  sent: 'With the carrier',
  chased: 'Chased',
  accepted: 'Accepted',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
};

const LETTER_LABEL: Record<CarrierLetter['status'], string> = {
  pending: 'Not sent yet',
  sent: 'With the carrier',
  released: 'Released to you',
  retained: 'Incumbent kept it',
  refused: 'Carrier refused it',
  withdrawn: 'Withdrawn',
};

const DAY = 86_400_000;
const daysBetween = (a: string, b: number): number =>
  Math.round((b - new Date(a).getTime()) / DAY);

/**
 * What to say about a letter's retention window.
 *
 * ⚠️ WHILE THE WINDOW RUNS THIS IS NOT A WARNING. The carrier is deliberately
 * giving the incumbent agent time to save the account, so a letter sitting here
 * is the process working — and an agent who rings a carrier on day six is
 * ringing them for doing what they are required to do.
 *
 * ⚠️ AND THE DATE IS AN ESTIMATE, SO IT IS WORDED AS ONE. Their clock runs
 * from their receipt and from when they told the incumbent; we only know when
 * we sent. "Around the 22nd" is honest. "Due the 22nd" is a claim we cannot
 * support, and the agent who stops watching because the date has not arrived is
 * exactly who that would fail.
 */
function windowNote(l: CarrierLetter, now: number): { text: string; late: boolean } | null {
  if (l.status !== 'sent' || !l.expect_by) return null;
  if (l.replied_at) return { text: 'They have replied — what did they say?', late: true };

  const left = daysBetween(new Date(now).toISOString(), new Date(l.expect_by).getTime());
  if (left >= 0) {
    return { text: `Expect an answer around ${when(l.expect_by)}`, late: false };
  }
  const over = Math.abs(left);
  return {
    text: `No answer — ${over === 1 ? 'a day' : `${over} days`} past the ${l.window_days}-day window`,
    late: true,
  };
}

/**
 * Request statuses that can still be withdrawn outright.
 *
 * ⚠️ ONLY BEFORE A LETTER HAS GONE. After that there is nothing request-level
 * left to decide — each carrier answers for itself — so withdrawing is a letter
 * action from then on. This one exists because it also kills the signing token,
 * which belongs to the request rather than to any letter.
 */
const CAN_WITHDRAW = new Set(['draft', 'signature', 'signed']);

export function Bor(): JSX.Element {
  const [rows, setRows] = useState<BorRequest[] | null>(null);
  const [building, setBuilding] = useState(false);

  const [kind, setKind] = useState<'bor' | 'aor'>('bor');
  const [insuredName, setInsuredName] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [previousBroker, setPreviousBroker] = useState('');

  const remembered = loadAgency();
  const [agencyName, setAgencyName] = useState(remembered.name);
  const [agencyAddress, setAgencyAddress] = useState(remembered.address);

  const [carriers, setCarriers] = useState<CarrierLine[]>([blankCarrier()]);

  /**
   * The insured's logo, as a data URL for preview and for sending.
   *
   * ⚠️ HELD AS THE DATA URL A PASTE ALREADY PRODUCES, so the preview and the
   * upload are the same bytes. Converting to a File to satisfy an input that
   * nobody used would be work in service of a control this page does not need.
   */
  const [logo, setLogo] = useState<{
    mime: string;
    dataUrl: string;
    source: 'upload' | 'website';
  } | null>(null);
  const [logoNote, setLogoNote] = useState('');

  /** The website lookup: what was typed, what came back, and whether it is running. */
  const [site, setSite] = useState('');
  const [siteBusy, setSiteBusy] = useState(false);
  const [found, setFound] = useState<
    { source: string; dataUrl: string; mime: string; label: string }[] | null
  >(null);

  /*
    ⚠️ THE THREE WAYS AN AGENT ACTUALLY HAS A LOGO ARE PASTE, DROP AND PICK, and
    paste is the one Josh described: "the agent can do a screen grab or clip and
    grab a screenshot of the picture of the logo and drop it in". A file input
    alone would mean saving the clipping to disk first, which is the step that
    makes people not bother.
  */
  function takeImage(file: File | null | undefined) {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/gif'].includes(file.type)) {
      setLogoNote('That has to be a PNG, JPG or GIF.');
      return;
    }
    if (file.size > 1_500_000) {
      setLogoNote('That image is too large — anything under about 1.5 MB is fine.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLogo({ mime: file.type, dataUrl: String(reader.result ?? ''), source: 'upload' });
      setLogoNote('');
    };
    reader.onerror = () => setLogoNote('That image could not be read. Try saving it and picking it.');
    reader.readAsDataURL(file);
  }

  /*
    ⚠️ THE LOOKUP OFFERS, IT NEVER APPLIES. Everything it finds is a guess — a
    site's social image is as often a photograph of a storefront as a logo — and
    a wrong logo on a broker-of-record letter is a document that looks forged.
    So the candidates are shown at the size they will print and an agent clicks
    one. "None of these" has to be an easy answer, which is why paste stays the
    first thing the field offers.
  */
  async function lookUp() {
    if (!site.trim()) return;
    setSiteBusy(true);
    setLogoNote('');
    try {
      const r = await api.borLogoLookup(site.trim());
      setFound(r.candidates);
    } catch {
      setFound([]);
    } finally {
      setSiteBusy(false);
    }
  }

  /*
    ⚠️ PASTE IS CAUGHT ON THE WINDOW, NOT ON A BOX YOU MUST CLICK FIRST. Ctrl+V
    after a snip is one gesture; "click here, then Ctrl+V" is two, and the
    second one is invisible. Nothing is hijacked — this only acts when the
    clipboard actually carries an image, so pasting text into any field on the
    page behaves exactly as it did.
  */
  useEffect(() => {
    if (!building) return undefined;
    const onPaste = (e: ClipboardEvent) => {
      const item = Array.from(e.clipboardData?.items ?? []).find((i) =>
        i.type.startsWith('image/'),
      );
      if (!item) return;
      takeImage(item.getAsFile());
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [building]);

  /** Which row's close drawer is open, and what is typed in it. */
  const [closing, setClosing] = useState<string | null>(null);
  const [closeNote, setCloseNote] = useState('');
  const [closeError, setCloseError] = useState('');

  /** And which row's resend drawer, with the address override. */
  const [resending, setResending] = useState<string | null>(null);
  const [resendTo, setResendTo] = useState('');
  const [resendNote, setResendNote] = useState('');
  const [resendOk, setResendOk] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  /** The signature being reviewed before its letters are released. */
  const [review, setReview] = useState<BorReview | null>(null);
  const [reviewFor, setReviewFor] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState('');

  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState<{ id: string; signUrl: string } | null>(null);

  useEffect(() => {
    api
      .borList()
      .then((r) => setRows(r.requests))
      .catch(() => setRows([]));
  }, []);

  const what = kind === 'aor' ? 'Agent of Record' : 'Broker of Record';

  /*
    One reading of the clock for the whole render. Calling Date.now() per row
    would let two letters disagree about which side of a window they are on,
    which is exactly the sort of inconsistency nobody can reproduce.
  */
  const now = Date.now();

  /** What the API wants, from what the form holds. */
  const payload = () => ({
    kind,
    insuredName: insuredName.trim(),
    signerName: signerName.trim(),
    signerEmail: signerEmail.trim(),
    signerTitle: signerTitle.trim(),
    agencyName: agencyName.trim(),
    agencyAddress: agencyAddress.trim(),
    previousBroker: previousBroker.trim(),
    /*
      ⚠️ SENT WHOLE, NOT UPLOADED SEPARATELY. A logo with no request to belong to
      is an orphaned file in the bucket, and a request created before its logo
      lands is one that can be signed without it. One call, or neither.
    */
    logo: logo ? { mime: logo.mime, dataBase64: logo.dataUrl, source: logo.source } : undefined,
    carriers: carriers
      .filter((c) => c.carrier.trim())
      .map((c) => ({
        carrier: c.carrier.trim(),
        carrierAddress: c.carrierAddress.trim(),
        policyNumbers: c.policyNumbers.trim(),
        policyEffective: c.policyEffective,
        // One carrier can need two desks; commas are how people type that.
        to: c.to
          .split(/[,;]/)
          .map((e) => e.trim())
          .filter(Boolean),
      })),
  });

  const ready =
    insuredName.trim() &&
    signerName.trim() &&
    signerEmail.includes('@') &&
    agencyName.trim() &&
    carriers.some((c) => c.carrier.trim() && c.to.includes('@'));

  async function showPreview() {
    setBusy(true);
    setError('');
    try {
      const r = await api.borPreview(payload());
      setPreview(r.letter);
    } catch {
      setError('Could not build the letter — check the insured, signer, agency and carriers.');
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    setBusy(true);
    setError('');
    try {
      const r = await api.borCreate(payload());
      try {
        localStorage.setItem(AGENCY_KEY, JSON.stringify({ name: agencyName, address: agencyAddress }));
      } catch {
        // A browser refusing storage costs a retype, not the request.
      }
      setSent({ id: r.id, signUrl: r.signUrl });
      setRows(await api.borList().then((x) => x.requests));
    } catch {
      setError('That did not send. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Open the review for a signed request.
   *
   * ⚠️ LOADED FRESH FROM THE SERVER EVERY TIME, NEVER FROM THE LIST ROW. The
   * list does not carry the signature at all, and a review screen assembled
   * from what the browser happened to have would be a screen that looks like
   * evidence and is not.
   */
  async function openReview(id: string) {
    setReviewFor(id);
    setReview(null);
    setReviewNote('');
    setClosing(null);
    setResending(null);
    try {
      setReview(await api.borReview(id));
    } catch {
      setReviewNote('Could not load the signature. Reload and try again.');
    }
  }

  /**
   * Release the letters.
   *
   * ⚠️ THE IRREVERSIBLE ONE. Once a carrier has the letter the incumbent
   * agent is notified and their window to save the account starts, so this is
   * the only thing on the page that asks twice.
   */
  async function release(id: string, carriers: number) {
    const what = carriers === 1 ? 'the carrier' : `all ${carriers} carriers`;
    if (!window.confirm(`Send the signed ${'letter'}${carriers === 1 ? '' : 's'} to ${what}? This cannot be undone.`)) {
      return;
    }
    setBusy(true);
    setReviewNote('');
    try {
      const r = await api.borSend(id);
      setRows(await api.borList().then((x) => x.requests));
      if (!r.delivered) {
        setReviewNote('Nothing was delivered. The request is still signed and ready — try again.');
        return;
      }
      /*
        ⚠️ A PARTIAL SEND IS REPORTED, NOT ROUNDED UP. Three of four desks
        receiving it is not "sent", and the one that missed out is the one whose
        policy quietly stays with the incumbent.
      */
      if (r.failed.length) {
        setReviewNote(
          `Sent, but these addresses failed: ${r.failed.join(', ')}. Check them and send those by hand.`,
        );
        return;
      }
      setReviewFor(null);
      setReview(null);
    } catch {
      setReviewNote('That did not send. Nothing has gone to a carrier — try again.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Send the link again.
   *
   * ⚠️ THE LIST IS RE-READ AFTERWARDS BECAUSE THE TOKEN MAY HAVE CHANGED. An
   * expired link is replaced server-side, and a page still holding the old one
   * would copy a dead URL into somebody's text message.
   */
  async function resend(id: string) {
    setBusy(true);
    setResendNote('');
    try {
      const r = await api.borResend(id, resendTo.trim());
      setRows(await api.borList().then((x) => x.requests));
      setResendTo('');
      /*
        ⚠️ THE DRAWER STAYS OPEN AND SAYS WHAT HAPPENED. Closing it on success
        would put an agent back on a list that looks exactly as it did, with no
        evidence anything was sent — and the reasonable response to that is to
        click resend again.
      */
      setResendOk(true);
      setResendNote(
        r.newLink
          ? `Sent to ${r.sentTo}. The old link had expired, so that email carries a new one.`
          : `Sent to ${r.sentTo}.`,
      );
    } catch {
      setResendOk(false);
      setResendNote('That did not send. Check the address and try again.');
    } finally {
      setBusy(false);
    }
  }

  /** Put the signing link on the clipboard, for the signer who would rather be texted. */
  async function copyLink(id: string, token: string) {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/sign/${token}`);
      setCopied(id);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      setResendOk(false);
      setResendNote('This browser would not let us copy. Use “Send it again” instead.');
    }
  }

  /**
   * Record what one carrier decided.
   *
   * ⚠️ THE LIST IS RE-READ FROM THE SERVER RATHER THAN PATCHED IN PLACE. The
   * server decides whether the transition was legal, and a local edit that
   * assumed it would be accepted is a list showing an outcome the database does
   * not hold — the exact state in which somebody stops chasing a live letter.
   */
  async function decide(
    letterId: string,
    status: 'released' | 'retained' | 'refused' | 'withdrawn',
  ) {
    setBusy(true);
    setCloseError('');
    try {
      await api.borLetterOutcome(letterId, status, closeNote.trim());
      setRows(await api.borList().then((x) => x.requests));
      setClosing(null);
      setCloseNote('');
    } catch {
      setCloseError('That did not save. Reload and check where the letter has got to.');
    } finally {
      setBusy(false);
    }
  }

  /** Tick the last step: it is in AMS360 now. */
  async function markEntered(letterId: string) {
    setBusy(true);
    try {
      await api.borLetterEntered(letterId);
      setRows(await api.borList().then((x) => x.requests));
    } catch {
      setCloseError('That did not save. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Withdraw the whole request, before anybody has signed.
   *
   * ⚠️ ASKS FIRST, BECAUSE IT KILLS THE SIGNING LINK. The insured may be
   * looking at that email right now, and there is no putting it back.
   */
  async function withdrawRequest(id: string) {
    if (!window.confirm('Withdraw this request? The signing link stops working immediately.')) {
      return;
    }
    setBusy(true);
    setCloseError('');
    try {
      await api.borWithdraw(id, closeNote.trim());
      setRows(await api.borList().then((x) => x.requests));
      setClosing(null);
      setCloseNote('');
    } catch {
      setCloseError('That did not save. Reload and try again.');
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setBuilding(false);
    setSent(null);
    setPreview('');
    setInsuredName('');
    setSignerName('');
    setSignerEmail('');
    setSignerTitle('');
    setPreviousBroker('');
    setCarriers([blankCarrier()]);
    // The next request is a different insured, so it is a different logo.
    setLogo(null);
    setLogoNote('');
    setSite('');
    setFound(null);
  }

  if (sent) {
    return (
      <div className="wrap">
        <section className="panel lr-review bor-done">
          <h1>Sent to {signerName || 'the insured'} to sign</h1>
          {/*
            ⚠️ SAYING WHAT HAS *NOT* HAPPENED IS THE POINT OF THIS SCREEN. Every
            other programme's confirmation means the carrier has been written to.
            This one means one person has been emailed, and an agent who assumes
            otherwise stops chasing at the moment the chasing matters.
          */}
          <p>
            <b>No carrier has been contacted yet.</b> {signerName || 'They'} will get an email with
            the {what} letter{carriers.filter((c) => c.carrier.trim()).length > 1 ? 's' : ''} to
            read and sign. When they sign, it comes back to you as “Signed — ready to send” so
            you can check the signature before anything goes to a carrier.
          </p>
          <p className="bor-ref">
            Reference <b>{sent.id}</b>
          </p>
          <div className="bor-actions">
            <button type="button" className="btn btn-primary" onClick={reset}>
              Start another
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="wrap">
      {/*
        ⚠️ THE HERO IS THE PAGE'S IDENTITY AND IT WAS MISSING (Josh, 2026-09-11:
        "needs to match the other request center programs"). Every programme in
        the Requests Center opens with it — breadcrumb, title, one line saying
        what the page is for, and the single action on the right. Without it
        this page began mid-sentence, and the only way back to the Center was
        the rail.
      */}
      <PageHero
        title="BOR / AOR Requests"
        sub="Move an account onto your paper — the insured signs once and every carrier is told."
        action={
          <button className="btn btn-primary" type="button" onClick={() => setBuilding((s) => !s)}>
            {building ? 'Back to the list' : '+ New request'}
          </button>
        }
      />

      {/*
        The house explainer panel, same as Loss Runs, Report a Loss and Binders.
        Two columns: what this is, and what to have ready before starting.
      */}
      {!building && (
        <section className="panel lr-about">
          <div>
            <h3>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3a9 9 0 100 18 9 9 0 000-18zM12 11v5M12 8h.01" />
              </svg>
              Moving an account to you
            </h3>
            <p>
              A broker-of-record letter is the insured instructing their carrier to change who
              represents them. It only has force because they signed it, so this prepares the
              letter and sends it to them. Nothing reaches a carrier until it comes back signed and
              you have released it.
            </p>
            <p className="lr-about-note">
              One signature covers every carrier on the account. It moves representation and
              nothing else: coverage, limits and premium are untouched.
            </p>
          </div>
          <div>
            <h3>What to have ready</h3>
            <ul>
              <li>The insured&rsquo;s full legal name, as the policy reads</li>
              <li>Somebody who can bind them — an owner, officer or partner — and their email</li>
              <li>Each carrier, with policy numbers if the insured has them</li>
              <li>Where the signed letter goes at each carrier</li>
              <li>Your own agency name and address, as it should appear</li>
            </ul>
          </div>
        </section>
      )}

      {/*
        Only while building. The hero carries the title and the one action, so a
        second heading row here was the page saying its own name twice.
      */}
      {building && (
        <section className="panel lr-form">
          <p className="lr-legend">New BOR / AOR request</p>
          <div className="bor-form sf-fields lr-fields">
            <div className="sf-field is-wide">
              <span className="lr-sublabel">What is it called?</span>
              <div className="lr-lines">
                {(['bor', 'aor'] as const).map((k) => (
                  <label key={k} className={kind === k ? 'is-on' : ''}>
                    <input type="radio" checked={kind === k} onChange={() => setKind(k)} />
                    <span>{k === 'bor' ? 'Broker of Record' : 'Agent of Record'}</span>
                  </label>
                ))}
              </div>
              <p className="sf-help">
                The same letter either way. Use whichever word the carrier and the insured use.
              </p>
            </div>

            <div className="sf-field">
              <label htmlFor="bor-insured">Named insured</label>
              <input
                id="bor-insured"
                value={insuredName}
                onChange={(e) => setInsuredName(e.target.value)}
              />
              <p className="sf-help">Their full legal name, as the policy reads.</p>
            </div>

            <div className="sf-field">
              <label htmlFor="bor-prev">Current agency</label>
              <input
                id="bor-prev"
                value={previousBroker}
                onChange={(e) => setPreviousBroker(e.target.value)}
              />
              {/*
                ⚠️ OPTIONAL, AND BLANK IS OFTEN THE RIGHT ANSWER. Naming them is
                the stronger sentence; naming the wrong one is worse than naming
                none, and an insured switching agencies is frequently vague about
                who holds the paper.
              */}
              <p className="sf-help">
                Only if you are sure. Left blank, the letter revokes whoever it was.
              </p>
            </div>

            {/*
              ⚠️ THE LOGO IS OPTIONAL AND SITS WITH THE INSURED, NOT THE AGENCY.
              It is their letterhead, not ours — the letter is from them. Josh,
              2026-09-11: "some carriers really want the BOR and AOR requests to
              have the insured's logo on it."
            */}
            <div
              className="sf-field is-wide bor-logo"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                takeImage(e.dataTransfer.files?.[0]);
              }}
            >
              <span className="lr-sublabel">Their logo (optional)</span>
              <div className="bor-logo-row">
                {logo ? (
                  <img className="bor-logo-shot" src={logo.dataUrl} alt="The logo as it will print" />
                ) : (
                  <div className="bor-logo-empty" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M3 5h18v14H3zM3 16l5-5 4 4 3-3 6 6" />
                    </svg>
                  </div>
                )}
                <div className="bor-logo-say">
                  <p className="sf-help">
                    Paste a screenshot anywhere on this page, drop an image here, or pick a file.
                    PNG, JPG or GIF.
                  </p>
                  <div className="bor-logo-acts">
                    <label className="btn btn-ghost bor-logo-pick">
                      {logo ? 'Choose a different one' : 'Choose a file'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif"
                        onChange={(e) => {
                          takeImage(e.target.files?.[0]);
                          // Cleared so picking the same file twice fires again.
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {logo && (
                      <button type="button" className="btn btn-ghost" onClick={() => setLogo(null)}>
                        Remove
                      </button>
                    )}
                  </div>
                  {logoNote && <p className="bor-logo-note">{logoNote}</p>}
                </div>
              </div>

              {/*
                The second way in: give us their website and we go and look.
                Josh, 2026-09-11: "you can put in a website or something and you
                can go and find the logo."
              */}
              <div className="bor-logo-site">
                <input
                  aria-label="The insured’s website"
                  value={site}
                  onChange={(e) => setSite(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      // Otherwise Enter in a field inside a form sends the form.
                      e.preventDefault();
                      void lookUp();
                    }
                  }}
                  placeholder="or find it on their website — joespizza.com"
                />
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={siteBusy || !site.trim()}
                  onClick={() => void lookUp()}
                >
                  {siteBusy ? 'Looking…' : 'Look'}
                </button>
              </div>

              {found !== null &&
                (found.length ? (
                  <>
                    <p className="sf-help bor-logo-pickhint">
                      Click the right one. These are guesses from their site — check it is really
                      their logo before you send.
                    </p>
                    <div className="bor-logo-found">
                      {found.map((c) => (
                        <button
                          type="button"
                          key={c.source}
                          className={`bor-logo-cand${logo?.dataUrl === c.dataUrl ? ' is-on' : ''}`}
                          onClick={() => {
                            setLogo({ mime: c.mime, dataUrl: c.dataUrl, source: 'website' });
                            setLogoNote('');
                          }}
                        >
                          <img src={c.dataUrl} alt="" />
                          <span>{c.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="sf-help bor-logo-pickhint">
                    Nothing usable on that site. Open it, screenshot the logo and paste it here —
                    that works on every site.
                  </p>
                ))}
            </div>

            <div className="sf-field is-wide">
              <span className="lr-sublabel">Who signs it</span>
              <p className="sf-help">
                Somebody who can bind the insured — an owner, officer or partner. The letter is
                addressed from them and the signing link goes to their inbox.
              </p>
            </div>

            <div className="sf-field">
              <label htmlFor="bor-signer">Signer’s name</label>
              <input id="bor-signer" value={signerName} onChange={(e) => setSignerName(e.target.value)} />
            </div>

            <div className="sf-field">
              <label htmlFor="bor-signer-title">Their title</label>
              <input
                id="bor-signer-title"
                value={signerTitle}
                onChange={(e) => setSignerTitle(e.target.value)}
                placeholder="Owner, President, Member"
              />
            </div>

            <div className="sf-field is-wide">
              <label htmlFor="bor-signer-email">Their email</label>
              <input
                id="bor-signer-email"
                type="email"
                value={signerEmail}
                onChange={(e) => setSignerEmail(e.target.value)}
              />
              <p className="sf-help">The signing link goes here, and nowhere else.</p>
            </div>

            <div className="sf-field is-wide">
              <span className="lr-sublabel">Your agency, as it goes on the letter</span>
              <p className="sf-help">Remembered on this device, so you only type it once.</p>
            </div>

            <div className="sf-field">
              <label htmlFor="bor-agency">Agency name</label>
              <input id="bor-agency" value={agencyName} onChange={(e) => setAgencyName(e.target.value)} />
            </div>

            <div className="sf-field">
              <label htmlFor="bor-agency-addr">Agency address</label>
              <input
                id="bor-agency-addr"
                value={agencyAddress}
                onChange={(e) => setAgencyAddress(e.target.value)}
              />
            </div>

            {/*
              ⚠️ ONE BLOCK PER CARRIER, BECAUSE ONE SIGNATURE COVERS ALL OF THEM.
              A BOR moving an account moves all of it, and asking an insured to
              sign four times to appoint one agency is how a transfer stalls half
              finished.
            */}
            <div className="sf-field is-wide">
              <span className="lr-sublabel">Carriers</span>
              <p className="sf-help">
                One letter each, all signed together. Add every carrier on the account.
              </p>
            </div>

            {carriers.map((c, i) => (
              <div className="bor-carrier sf-field is-wide" key={i}>
                <div className="bor-carrier-head">
                  <b>Carrier {i + 1}</b>
                  {carriers.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setCarriers(carriers.filter((_, n) => n !== i))}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="bor-carrier-grid">
                  <input
                    placeholder="Carrier name"
                    value={c.carrier}
                    onChange={(e) =>
                      setCarriers(carriers.map((x, n) => (n === i ? { ...x, carrier: e.target.value } : x)))
                    }
                  />
                  <input
                    placeholder="Policy number(s) — “and all previous” is fine"
                    value={c.policyNumbers}
                    onChange={(e) =>
                      setCarriers(
                        carriers.map((x, n) => (n === i ? { ...x, policyNumbers: e.target.value } : x)),
                      )
                    }
                  />
                  <input
                    placeholder="Carrier address (optional)"
                    value={c.carrierAddress}
                    onChange={(e) =>
                      setCarriers(
                        carriers.map((x, n) => (n === i ? { ...x, carrierAddress: e.target.value } : x)),
                      )
                    }
                  />
                  <input
                    type="date"
                    aria-label={`Policy effective date for carrier ${i + 1}`}
                    value={c.policyEffective}
                    onChange={(e) =>
                      setCarriers(
                        carriers.map((x, n) => (n === i ? { ...x, policyEffective: e.target.value } : x)),
                      )
                    }
                  />
                  <input
                    className="is-wide"
                    placeholder="Send the signed letter to — email, or several separated by commas"
                    value={c.to}
                    onChange={(e) =>
                      setCarriers(carriers.map((x, n) => (n === i ? { ...x, to: e.target.value } : x)))
                    }
                  />
                </div>
              </div>
            ))}

            <div className="sf-field is-wide">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setCarriers([...carriers, blankCarrier()])}
              >
                Add another carrier
              </button>
            </div>

            {preview && (
              <div className="sf-field is-wide">
                <span className="lr-sublabel">The letter</span>
                <pre className="bor-preview">{preview}</pre>
                <p className="sf-help">
                  {carriers.filter((c) => c.carrier.trim()).length > 1
                    ? 'Shown for the first carrier. Each one gets its own, naming its own policies.'
                    : 'This is what the carrier receives once it is signed.'}
                </p>
              </div>
            )}

            {error && <p className="is-warn">{error}</p>}

            <div className="sf-field is-wide bor-actions">
              <button type="button" className="btn" disabled={!ready || busy} onClick={showPreview}>
                {preview ? 'Refresh the letter' : 'Show the letter'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!ready || busy}
                onClick={send}
              >
                {busy ? 'Sending…' : 'Send to the insured to sign'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={reset}>
                Cancel
              </button>
            </div>
            <p className="sf-help bor-note">
              This emails the signer only. Nothing reaches a carrier until they have signed.
            </p>
          </div>
        </section>
      )}

      {/*
        ⚠️ .lr-table-panel HAS NO PADDING OF ITS OWN — ON PURPOSE. Every
        programme fills it edge to edge: a .lr-toolbar header with its own
        inset, then a full-bleed table or .lr-empty. Dropping a bare paragraph
        straight into it puts the text against the panel's corner with nothing
        around it, which is what this looked like (Josh, 2026-09-11: "a little
        off looking. Just looks crammed"). Anything placed here must bring its
        own padding, so use the house parts.
      */}
      <section className="panel lr-table-panel">
        <div className="lr-toolbar">
          <div className="lr-tabs">
            <span className="bor-tab-still">Your requests</span>
          </div>
          {rows && rows.length > 0 && (
            <span className="lr-count-still">
              {rows.length} {rows.length === 1 ? 'request' : 'requests'}
            </span>
          )}
        </div>

        {!rows && <p className="lr-empty">Loading…</p>}
        {rows && !rows.length && (
          <p className="lr-empty">
            No BOR or AOR requests yet.
            <span>
              A BOR moves an account onto your paper — the insured signs once and every carrier is
              told.
            </span>
          </p>
        )}
        {/*
            ⚠️ ONE ROW PER REQUEST, THEN ONE ROW PER LETTER UNDERNEATH — the Loss
            Run Pro shape. Josh, 2026-09-14: "the request is just a container."
            After the signature the request holds no verdict of its own, so an
            agent scanning for what needs them is scanning letters. A single
            status on the request could only ever show one of "Farmers released
            it" and "Travelers refused it", and either one alone is a lie.
        */}
        {rows && rows.length > 0 && (
          <table className="lr-table bor-table">
            <thead>
              <tr>
                <th>Insured / carrier</th>
                <th>Status</th>
                <th>Where it stands</th>
                <th>Sent</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const letters = r.letters ?? [];
                return (
                <Fragment key={r.id}>
                  <tr className="bor-req-row">
                    <td>
                      <b>{r.insured_name}</b>
                      <span className="lr-sub">
                        {r.id} · {r.kind === 'aor' ? 'AOR' : 'BOR'}
                      </span>
                    </td>
                    <td>
                      {STATUS_LABEL[r.status] ?? r.status}
                      {r.resend_count ? (
                        <span className="lr-sub">
                          Resent {r.resend_count === 1 ? 'once' : `${r.resend_count} times`}
                          {r.last_resent_at ? `, last ${when(r.last_resent_at)}` : ''}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {/*
                        Before the signature the request genuinely is the unit,
                        so this says whose desk it is on. After it, the letters
                        below answer the same question one carrier at a time.
                      */}
                      {r.status === 'signature'
                        ? `${r.signer_name || 'The insured'} has not signed yet`
                        : r.status === 'signed'
                          ? 'Signed — check it and send it'
                          : r.signed_at
                            ? `Signed ${when(r.signed_at)}`
                            : ''}
                    </td>
                    <td>{when(r.sent_to_carriers_at)}</td>
                    <td className="bor-rowact">
                      {r.status === 'signed' && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() =>
                            reviewFor === r.id ? setReviewFor(null) : void openReview(r.id)
                          }
                        >
                          {reviewFor === r.id ? 'Close' : 'Review & send'}
                        </button>
                      )}
                      {r.status === 'signature' && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => {
                            setResending(resending === r.id ? null : r.id);
                            setResendTo('');
                            setResendNote('');
                            setResendOk(false);
                            setClosing(null);
                          }}
                        >
                          {resending === r.id ? 'Cancel' : 'Resend…'}
                        </button>
                      )}
                      {CAN_WITHDRAW.has(r.status) && (
                        <button
                          type="button"
                          className="btn btn-ghost bor-withdraw"
                          disabled={busy}
                          onClick={() => void withdrawRequest(r.id)}
                        >
                          Withdraw
                        </button>
                      )}
                    </td>
                  </tr>

                  {/*
                    ⚠️ PENDING LETTERS ARE NOT LISTED. Before the send they carry
                    no information the request line does not already give — a row
                    per carrier reading "not sent yet" is four lines of noise on
                    a request whose whole state is "waiting for a signature".
                  */}
                  {letters
                    .filter((l) => l.status !== 'pending')
                    .map((l) => {
                      const note = windowNote(l, now);
                      return (
                        <tr className="bor-letter-row" key={l.id}>
                          <td>
                            <span className="bor-letter-carrier">{l.carrier}</span>
                            {l.policy_numbers ? (
                              <span className="lr-sub">Policy {l.policy_numbers}</span>
                            ) : null}
                          </td>
                          <td>
                            {LETTER_LABEL[l.status]}
                            {l.outcome_note ? (
                              <span className="lr-sub">{l.outcome_note}</span>
                            ) : null}
                          </td>
                          <td>
                            {note && (
                              <span className={note.late ? 'is-overdue' : undefined}>
                                {note.text}
                              </span>
                            )}
                            {/*
                              ⚠️ A RELEASED LETTER IS NOT FINISHED UNTIL IT IS IN
                              AMS360. Josh: "Then it gets entered into the
                              management system." An account released and never
                              set up is one nobody is servicing and nobody is
                              being paid for.
                            */}
                            {l.status === 'released' && !l.entered_ams_at && (
                              <span className="is-overdue">Not in AMS360 yet</span>
                            )}
                            {l.entered_ams_at && (
                              <span className="lr-sub">In AMS360 {when(l.entered_ams_at)}</span>
                            )}
                          </td>
                          <td>{when(l.sent_at)}</td>
                          <td className="bor-rowact">
                            {l.status === 'sent' && (
                              <button
                                type="button"
                                className={`btn ${l.replied_at ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={() => {
                                  setClosing(closing === l.id ? null : l.id);
                                  setCloseNote('');
                                  setCloseError('');
                                }}
                              >
                                {closing === l.id ? 'Cancel' : 'Record answer…'}
                              </button>
                            )}
                            {l.status === 'released' && !l.entered_ams_at && (
                              <button
                                type="button"
                                className="btn btn-primary"
                                disabled={busy}
                                onClick={() => void markEntered(l.id)}
                              >
                                Entered in AMS360
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                  {letters.some((l) => closing === l.id) && (
                    <tr className="lr-drawer-row">
                      <td colSpan={5}>
                        <div className="lr-drawer bor-close">
                          <div className="bor-close-say">
                            <span>What did they say?</span>
                            <input
                              value={closeNote}
                              onChange={(e) => setCloseNote(e.target.value)}
                              placeholder="Inside the renewal window, prior BOR on file, insured changed their mind…"
                              aria-label="What the carrier said"
                            />
                            {closeError && <p className="bor-logo-note">{closeError}</p>}
                          </div>
                          {/*
                            ⚠️ THREE OUTCOMES, NOT TWO. "The incumbent kept it" and
                            "the carrier refused it" shared one button until now,
                            and they share nothing else: one means the account is
                            gone, the other means fix the letter and try again.
                          */}
                          <div className="lr-drawer-actions">
                            <button
                              type="button"
                              className="btn btn-primary"
                              disabled={busy}
                              onClick={() => void decide(String(closing), 'released')}
                            >
                              Released to us
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost"
                              disabled={busy}
                              onClick={() => void decide(String(closing), 'retained')}
                            >
                              Incumbent kept it
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost"
                              disabled={busy}
                              onClick={() => void decide(String(closing), 'refused')}
                            >
                              Carrier refused it
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {reviewFor === r.id && (
                    <tr className="lr-drawer-row">
                      <td colSpan={5}>
                        <div className="bor-review">
                          {!review && !reviewNote && <p className="sf-help">Loading the signature…</p>}

                          {review && (
                            <>
                              {/*
                                ⚠️ WHO SIGNED COMES FIRST, BEFORE THE LETTER. The
                                agent wrote the letter and does not need to
                                re-read their own words. What they have never
                                seen is the signature — and the common failure
                                this screen exists to catch is an office manager
                                signing something only an owner can sign.
                              */}
                              <h3>Who signed it</h3>
                              <div className="bor-review-grid">
                                <div>
                                  <span>Typed</span>
                                  <b>{review.signature?.typedName || '—'}</b>
                                </div>
                                <div>
                                  <span>You asked</span>
                                  <b>
                                    {review.askedOf.name}
                                    {review.askedOf.title ? `, ${review.askedOf.title}` : ''}
                                  </b>
                                </div>
                                <div>
                                  <span>At</span>
                                  <b>{review.askedOf.email}</b>
                                </div>
                                <div>
                                  <span>Signed</span>
                                  <b>
                                    {review.signature
                                      ? new Date(review.signature.signedAt).toLocaleString()
                                      : '—'}
                                  </b>
                                </div>
                                <div>
                                  <span>From IP</span>
                                  <b>{review.signature?.ip || '—'}</b>
                                </div>
                              </div>

                              {/*
                                ⚠️ THE STORED WORDING, NOT TODAY'S. Rendering the
                                current text beside an older signature would
                                show the agent something the signer never read.
                              */}
                              <h3>What they affirmed</h3>
                              <pre className="bor-review-wording">{review.signature?.wording}</pre>

                              <h3>Where it goes</h3>
                              <ul className="bor-review-to">
                                {review.letters.map((l) => (
                                  <li key={l.carrier}>
                                    <b>{l.carrier}</b> — {l.to.join(', ') || 'no address'}
                                  </li>
                                ))}
                              </ul>

                              <details className="bor-review-letters">
                                <summary>Read the {review.letters.length === 1 ? 'letter' : 'letters'} again</summary>
                                {review.letters.map((l) => (
                                  <pre className="bor-preview" key={l.carrier}>
                                    {l.text}
                                  </pre>
                                ))}
                              </details>

                              {reviewNote && <p className="bor-logo-note">{reviewNote}</p>}

                              <div className="bor-review-acts">
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  disabled={busy}
                                  onClick={() => void release(r.id, review.letters.length)}
                                >
                                  {busy
                                    ? 'Sending…'
                                    : `Send to ${review.letters.length === 1 ? 'the carrier' : `all ${review.letters.length} carriers`}`}
                                </button>
                                {/*
                                  The way out, in the same place as the way
                                  forward. Noticing the wrong person signed is
                                  only useful if stopping is as easy as sending.
                                */}
                                <button
                                  type="button"
                                  className="btn btn-ghost bor-withdraw"
                                  disabled={busy}
                                  onClick={() => {
                                    setReviewFor(null);
                                    setClosing(r.id);
                                    setCloseNote('');
                                    setCloseError('');
                                  }}
                                >
                                  Something is wrong — withdraw it
                                </button>
                              </div>
                            </>
                          )}

                          {!review && reviewNote && <p className="bor-logo-note">{reviewNote}</p>}
                        </div>
                      </td>
                    </tr>
                  )}

                  {resending === r.id && (
                    <tr className="lr-drawer-row">
                      <td colSpan={5}>
                        <div className="lr-drawer bor-close">
                          <div className="bor-close-say">
                            <span>Send the signing link again</span>
                            <input
                              type="email"
                              value={resendTo}
                              onChange={(e) => setResendTo(e.target.value)}
                              placeholder={r.signer_email}
                              aria-label="Send to a different address"
                            />
                            {/*
                              ⚠️ THE COMMONEST CAUSE OF "I NEVER GOT IT" IS A
                              TYPO IN THE ADDRESS. Resending faithfully to the
                              same wrong inbox is a button that appears to work
                              and changes nothing, so the address is editable
                              here and blank means "the one on file".
                            */}
                            <p className="sf-help">
                              Leave blank to use {r.signer_email}. The link they already have keeps
                              working.
                            </p>
                            {resendNote && (
                              <p className={resendOk ? 'bor-sent-note' : 'bor-logo-note'}>
                                {resendNote}
                              </p>
                            )}
                          </div>
                          <div className="lr-drawer-actions">
                            {/*
                              Copying it is the other half: plenty of signers
                              would rather be texted the link than emailed it
                              again, and that needs no send from us at all.
                            */}
                            {r.sign_token && (
                              <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => void copyLink(r.id, r.sign_token ?? '')}
                              >
                                {copied === r.id ? 'Copied' : 'Copy link'}
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-primary"
                              disabled={busy}
                              onClick={() => void resend(r.id)}
                            >
                              Send it again
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
